import React, { useCallback, useEffect, useState } from 'react';
import classes from './style.module.scss';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import { useStoreActions, useStoreState } from '~/store';
import useAccessControl from '~/app/hooks/useAccessControl';
import { DEPARTMENT_PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import { Material, SaveMaterialResponse } from '~/types';
import AddMaterialDialog from '~/materials/Add';
import MaterialForm from '~/materials/Add/MaterialForm';
import QuantityEditForm from '~/materials/QuantityEditForm';
import { isMaterialsEditable, isVariousMaterial, toSaveMaterialPayload } from '~/materials/helper';
import ListItem from './ListItem';

export type MaterialsComponent = React.FC;

const Materials: MaterialsComponent = () => {
  const { t } = useTranslation('workorder');
  const { t: tParts } = useTranslation('parts');
  const { checkPermission } = useAccessControl();

  const [expanded, setExpanded] = useState(false);
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [materialForEdit, setMaterialForEdit] = useState<Material>();
  const [isOpenMaterialForm, setIsOpenMaterialForm] = useState(false);
  const [isOpenQuantityEditForm, setIsOpenQuantityEditForm] = useState(false);

  const materials = useStoreState(state => state.workOrder.data.materials ?? []);
  const id = useStoreState(state => state.workOrder.data.id);
  const status = useStoreState(state => state.workOrder.data.status);
  const deptPrefix = useStoreState(state => state.workOrder.data.departmentPrefix);
  const netWorkStatus = useStoreState(state => state.app.offline.status);

  const fetchMaterialsAction = useStoreActions(actions => actions.workOrder.fetchMaterials);
  const setTempMaterialsAction = useStoreActions(actions => actions.workOrder.setTempMaterials);
  const saveMaterialsAction = useStoreActions(actions => actions.workOrder.saveMaterials);

  const addMaterialPermission = deptPrefix
    ? checkPermission(DEPARTMENT_PERMISSION_GROUPS.addParts(deptPrefix))
    : false;

  const canEdit =
    !netWorkStatus
    && isMaterialsEditable(PartLinkTypes.WORKORDER, status)
    && addMaterialPermission;

  useEffect(() => {
    if (!netWorkStatus && id) {
      fetchMaterialsAction(String(id));
    }
  }, [id, fetchMaterialsAction, netWorkStatus]);

  const toggleAddDialog = useCallback((open: boolean) => (): void => {
    setTempMaterialsAction(materials);
    setIsOpenAddForm(open);
  }, [materials, setTempMaterialsAction]);

  const persistMaterials = useCallback((nextMaterials: Material[]) => {
    setTempMaterialsAction(nextMaterials);
    return saveMaterialsAction(toSaveMaterialPayload(id, nextMaterials)).then((res: SaveMaterialResponse) => {
      if (res.success) {
        fetchMaterialsAction(String(id));
      }
      return res;
    });
  }, [id, setTempMaterialsAction, saveMaterialsAction, fetchMaterialsAction]);

  const onRemove = useCallback((materialToRemove: Material) => {
    const nextMaterials = materials.map(m =>
      String(m.id) === String(materialToRemove.id)
        ? { ...m, deleted: true, added: undefined }
        : { ...m, added: undefined }
    );
    persistMaterials(nextMaterials);
  }, [materials, persistMaterials]);

  const onEdit = useCallback((material: Material) => {
    setTempMaterialsAction(materials.map(m => ({ ...m, added: undefined })));
    setMaterialForEdit(material);
    if (isVariousMaterial(material)) {
      setIsOpenQuantityEditForm(true);
    } else {
      setIsOpenMaterialForm(true);
    }
  }, [materials, setTempMaterialsAction]);

  const closeMaterialForm = useCallback(() => {
    setIsOpenMaterialForm(false);
    setMaterialForEdit(undefined);
  }, []);

  const closeQuantityForm = useCallback(() => {
    setIsOpenQuantityEditForm(false);
    setMaterialForEdit(undefined);
  }, []);

  const onQuantitySave = useCallback((material: Material, quantity: number) => {
    const nextMaterials = materials.map(m =>
      String(m.id) === String(material.id)
        ? { ...m, quantity, added: undefined }
        : { ...m, added: undefined }
    );
    persistMaterials(nextMaterials);
  }, [materials, persistMaterials]);

  return (
    <>
      <div className={classes.card}>
        <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
          <h2 className={classes['header-title']}>
            <Icon name="build" size={20} className={classes['section-header-icon']} />
            {tParts('material')}
          </h2>
          {canEdit && (
            <button
              type="button"
              className={classes['add-action']}
              onClick={(e) => {
                e.stopPropagation();
                toggleAddDialog(true)();
              }}
            >
              + {tParts('add')}
            </button>
          )}
          <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']} />
        </div>
        {expanded && (
          materials.length === 0 ? (
            <p className={classes.empty}>{t('detail.noMaterials')}</p>
          ) : (
            <div className={classes.list}>
              {materials.map(material => (
                <ListItem
                  key={String(material.id)}
                  material={material}
                  canEdit={canEdit}
                  onEdit={onEdit}
                  onRemove={onRemove}
                />
              ))}
            </div>
          )
        )}
      </div>
      {canEdit && (
        <>
          <AddMaterialDialog
            open={isOpenAddForm}
            onClose={toggleAddDialog(false)}
            type={PartLinkTypes.WORKORDER}
          />
          <MaterialForm
            open={isOpenMaterialForm}
            onClose={closeMaterialForm}
            material={materialForEdit}
            type={PartLinkTypes.WORKORDER}
            persistOnSave
          />
          <QuantityEditForm
            open={isOpenQuantityEditForm}
            onClose={closeQuantityForm}
            material={materialForEdit}
            onSave={onQuantitySave}
          />
        </>
      )}
    </>
  );
};

export default Materials;
